import React, { useState, useEffect, useRef } from 'react';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Plus, Edit2, Trash2, Loader2, ClipboardList,
  Clock, Calendar, RefreshCw, AlertTriangle, GripVertical,
  Camera, ImageIcon, X, Flame, Thermometer, ShoppingCart, Sparkles, CheckSquare,
  Settings, ChevronDown, ChevronUp,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TASK_TYPE_OPTIONS = [
  { value: 'standard',   label: 'Standard',   icon: CheckSquare, color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30' },
  { value: 'temperatur', label: 'Temperatur', icon: Thermometer, color: 'bg-sky-100 text-sky-700 dark:bg-sky-900/30' },
];

const PRIORITY_OPTIONS = [
  { value: 'niedrig',  label: 'Niedrig',  cls: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300' },
  { value: 'mittel',   label: 'Mittel',   cls: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300' },
  { value: 'hoch',     label: 'Hoch',     cls: 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300' },
  { value: 'kritisch', label: 'Kritisch', cls: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300' },
];

const TURNUS_OPTIONS = [
  { value: 'täglich',     label: 'Täglich',     icon: RefreshCw },
  { value: 'wöchentlich', label: 'Wöchentlich', icon: Calendar },
  { value: 'monatlich',   label: 'Monatlich',   icon: Calendar },
  { value: 'einmalig',    label: 'Einmalig',    icon: Clock },
];

const WOCHENTAGE = [
  { value: 'montag',     label: 'Mo' },
  { value: 'dienstag',   label: 'Di' },
  { value: 'mittwoch',   label: 'Mi' },
  { value: 'donnerstag', label: 'Do' },
  { value: 'freitag',    label: 'Fr' },
  { value: 'samstag',    label: 'Sa' },
  { value: 'sonntag',    label: 'So' },
];

const emptyForm = () => ({
  name: '', description: '', priority: 'mittel',
  task_type: 'standard', is_arbeitsbeginn: false,
  location_ids: [], shift_ids: [],
  turnus: { typ: 'täglich', wochentage: [], monatstag: 1, einmalig_datum: '' },
  deadline_time: '', ab_uhrzeit: '', ziel_uhrzeit: '', photo_required: false,
  is_closing_task: false, is_opening_task: false, blocks_others: false,
  reference_image_url: null,
});

export const TaskTypes = () => {
  const { user } = useAuth();
  const isConfigurator = ['admin', 'stationsleitung', 'superadmin'].includes(user?.role);
  const [showKonfig, setShowKonfig] = useState(isConfigurator); // Admin/SL: aufgeklappt; employee: nie sichtbar

  const [tasks, setTasks] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [filterLocation, setFilterLocation] = useState('all');
  const imageFileRef = useRef();
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [multiImages, setMultiImages] = useState([]);
  const [uploadingMulti, setUploadingMulti] = useState(false);
  // Band-Verwaltung
  const [bandModal, setBandModal] = useState(null); // { locationId, bandType }
  const [bandData, setBandData] = useState(null);
  const [bandCategories, setBandCategories] = useState([]); // [{name, description}]
  const [bandSaving, setBandSaving] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const [tasksRes, locsRes] = await Promise.all([
        axios.get(`${API}/tasks/templates`, { headers }),
        axios.get(`${API}/user/locations`, { headers }),
      ]);
      const locs = locsRes.data || [];
      const rawTasks = tasksRes.data || [];
      rawTasks.sort((a, b) => (a.sort_order ?? 999) - (b.sort_order ?? 999));
      setTasks(rawTasks);
      setLocations(locs);
    } catch { toast.error('Fehler beim Laden der Daten'); }
    finally { setLoading(false); }
  };

  // Band-Konfiguration laden und Modal öffnen
  const openBandModal = async (locationId, bandType) => {
    const token = localStorage.getItem('token');
    const headers = { Authorization: `Bearer ${token}` };
    try {
      const r = await axios.get(`${API}/tasks/band?location_id=${locationId}&band_type=${bandType}`, { headers });
      const existing = r.data?.id ? r.data : null;
      setBandData(existing);
      setBandCategories(existing ? existing.categories.map(c => ({ name: c.name, description: c.description || '' })) : [{ name: '', description: '' }]);
    } catch { setBandData(null); setBandCategories([{ name: '', description: '' }]); }
    setBandModal({ locationId, bandType });
  };

  const saveBand = async () => {
    if (!bandModal) return;
    const cats = bandCategories.filter(c => c.name?.trim());
    if (!cats.length) { toast.error('Mindestens eine Kategorie erforderlich'); return; }
    setBandSaving(true);
    const token = localStorage.getItem('token');
    const headers = { Authorization: `Bearer ${token}` };
    const payload = cats.map((c, i) => ({ name: c.name.trim(), description: c.description || '', order: i }));
    try {
      if (bandData?.id) {
        await axios.put(`${API}/tasks/band/${bandData.id}`, { categories: payload }, { headers });
      } else {
        await axios.post(`${API}/tasks/band`, { location_id: bandModal.locationId, band_type: bandModal.bandType, categories: payload }, { headers });
      }
      toast.success('Band gespeichert');
      setBandModal(null);
    } catch (e) { toast.error(e.response?.data?.detail || 'Fehler'); }
    finally { setBandSaving(false); }
  };

  // ── Drag & Drop (vereinfacht — kein Schicht-Cross-Drop mehr) ─────────────────
  const dragIndex = useRef(null);
  const dragOverIndex = useRef(null);
  const [isDragging, setIsDragging] = useState(false);

  const handleDragStart = (index) => {
    dragIndex.current = index;
    setIsDragging(true);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    dragOverIndex.current = index;
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    const from = dragIndex.current;
    const to = dragOverIndex.current;
    setIsDragging(false);
    if (from === null || to === null || from === to) { dragIndex.current = null; dragOverIndex.current = null; return; }
    dragIndex.current = null; dragOverIndex.current = null;

    const filtered = getFilteredTasks();
    const reordered = [...filtered];
    const [moved] = reordered.splice(from, 1);
    reordered.splice(to, 0, moved);
    const updatedAll = tasks.map(t => {
      const idx = reordered.findIndex(r => r.id === t.id);
      return idx >= 0 ? { ...t, sort_order: idx } : t;
    });
    setTasks(updatedAll);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/tasks/templates/reorder`, reordered.map((t, i) => ({ id: t.id, sort_order: i })), { headers: { Authorization: `Bearer ${token}` } });
    } catch { /* behalte UI */ }
  };

  const openCreate = () => {
    setEditId(null);
    setForm(emptyForm());
    setImageFile(null); setImagePreview(null);
    setMultiImages([]);
    setUploadingMulti(false); // Reset stuck state
    setModalOpen(true);
  };

  const openEdit = (t) => {
    setEditId(t.id);
    setForm({
      name: t.name || '',
      description: t.description || '',
      priority: t.priority || 'mittel',
      task_type: t.task_type || 'standard',
      is_arbeitsbeginn: t.is_arbeitsbeginn || false,
      location_ids: t.location_ids || [],
      shift_ids: t.shift_ids || [],
      turnus: t.turnus || { typ: 'täglich', wochentage: [], monatstag: 1, einmalig_datum: '' },
      deadline_time: t.deadline_time || '',
      ab_uhrzeit: t.ab_uhrzeit || '',
      ziel_uhrzeit: t.ziel_uhrzeit || '',
      photo_required: t.photo_required || false,
      is_closing_task: t.is_closing_task || false,
      is_opening_task: t.is_opening_task || false,
      blocks_others: t.blocks_others || false,
      reference_image_url: t.reference_image_url || null,
    });
    setImageFile(null);
    setImagePreview(t.reference_image_url || null);
    setMultiImages(t.reference_images || []);
    setUploadingMulti(false); // Reset stuck state
    setModalOpen(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error('Name ist Pflicht'); return; }
    if (form.location_ids.length === 0) { toast.error('Mindestens ein Standort muss gewählt werden'); return; }
    if (form.turnus.typ === 'wöchentlich' && form.turnus.wochentage.length === 0) {
      toast.error('Für wöchentlichen Turnus bitte Wochentage wählen'); return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const payload = { ...form };
      if (!payload.deadline_time) delete payload.deadline_time;

      let savedId = editId;
      if (editId) {
        await axios.put(`${API}/tasks/templates/${editId}`, payload, { headers });
        toast.success('Aufgabe aktualisiert');
      } else {
        const res = await axios.post(`${API}/tasks/templates`, payload, { headers });
        savedId = res.data.id;
        toast.success('Aufgabe erstellt');
      }

      // Einzelbild (Legacy) hochladen
      if (imageFile && savedId) {
        setUploadingImage(true);
        try {
          const fd = new FormData();
          fd.append('file', imageFile);
          await axios.post(`${API}/tasks/templates/${savedId}/upload-image`, fd, {
            headers: { ...headers, 'Content-Type': 'multipart/form-data' },
          });
        } catch { toast.error('Bild konnte nicht hochgeladen werden'); }
        finally { setUploadingImage(false); }
      }
      // Einzelbild gelöscht
      if (!imagePreview && !imageFile && form.reference_image_url && editId) {
        try { await axios.delete(`${API}/tasks/templates/${editId}/image`, { headers }); }
        catch { /* ignorieren */ }
      }

      setModalOpen(false);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler beim Speichern');
    } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Aufgabe wirklich löschen?')) return;
    try {
      await axios.delete(`${API}/tasks/templates/${id}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      toast.success('Aufgabe gelöscht');
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Fehler beim Löschen'); }
  };

  const toggleLocation = (id) => {
    setForm(f => ({
      ...f,
      location_ids: f.location_ids.includes(id)
        ? f.location_ids.filter(x => x !== id)
        : [...f.location_ids, id],
    }));
  };

  const toggleWochentag = (val) => {
    setForm(f => ({
      ...f,
      turnus: {
        ...f.turnus,
        wochentage: f.turnus.wochentage.includes(val)
          ? f.turnus.wochentage.filter(x => x !== val)
          : [...f.turnus.wochentage, val],
      },
    }));
  };

  const getPriorityStyle = (p) => PRIORITY_OPTIONS.find(o => o.value === p)?.cls || '';

  const getTurnusLabel = (turnus) => {
    if (!turnus) return '–';
    if (turnus.typ === 'täglich') return 'Täglich';
    if (turnus.typ === 'wöchentlich') {
      const days = (turnus.wochentage || []).map(d => WOCHENTAGE.find(w => w.value === d)?.label || d);
      return `Wöchentlich: ${days.join(', ') || '–'}`;
    }
    if (turnus.typ === 'monatlich') return `Monatlich am ${turnus.monatstag || '?'}.`;
    if (turnus.typ === 'einmalig') return `Einmalig: ${turnus.einmalig_datum || '–'}`;
    return turnus.typ;
  };

  const getFilteredTasks = () => filterLocation === 'all'
    ? tasks
    : tasks.filter(t => (t.location_ids || []).includes(filterLocation));

  const filteredTasks = getFilteredTasks();
  const getTaskTypeInfo = (type) => TASK_TYPE_OPTIONS.find(o => o.value === type) || TASK_TYPE_OPTIONS[0];

  return (
    <DashboardLayout>
      <div className="p-4 md:p-6 max-w-4xl mx-auto space-y-5">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
            <ClipboardList className="h-6 w-6 text-primary" />
            Tagesgeschäft
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Standard-Aufgaben für den täglichen Betrieb
          </p>
        </div>

        {/* ── Konfigurieren-Bereich — NUR Admin/Stationsleitung ── */}
        {isConfigurator && (
          <div className="border border-border rounded-xl overflow-hidden" data-testid="tagesgeschaeft-konfig">
            <button
              className="w-full flex items-center justify-between px-4 py-3 bg-muted/30 hover:bg-muted/50 transition-colors"
              onClick={() => setShowKonfig(v => !v)}
              data-testid="konfig-toggle-btn"
            >
              <div className="flex items-center gap-2">
                <Settings className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm font-medium">Konfigurieren (Admin/Stationsleitung)</span>
                {tasks.length > 0 && (
                  <span className="text-xs text-muted-foreground">· {tasks.length} Aufgaben</span>
                )}
              </div>
              {showKonfig
                ? <ChevronUp className="h-4 w-4 text-muted-foreground" />
                : <ChevronDown className="h-4 w-4 text-muted-foreground" />}
            </button>

            {showKonfig && (
              <div className="p-4 border-t border-border space-y-5">
                {/* "Neue Aufgabe"-Button + Filter */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div className="flex gap-2 flex-wrap">
                    {locations.length > 1 && (
                      <>
                        <Badge variant={filterLocation === 'all' ? 'default' : 'outline'}
                          className="cursor-pointer px-3 py-1"
                          onClick={() => setFilterLocation('all')}>
                          Alle Standorte
                        </Badge>
                        {locations.map(l => (
                          <Badge key={l.id}
                            variant={filterLocation === l.id ? 'default' : 'outline'}
                            className="cursor-pointer px-3 py-1"
                            onClick={() => setFilterLocation(l.id)}>
                            {l.name}
                          </Badge>
                        ))}
                      </>
                    )}
                  </div>
                  <Button onClick={openCreate} data-testid="create-task-btn" className="flex items-center gap-2 shrink-0">
                    <Plus className="h-4 w-4" />
                    Neue Aufgabe
                  </Button>
                </div>

        {/* Task-Liste — flach, nach Typ gruppiert */}
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : filteredTasks.length === 0 ? (
          <Card className="border-dashed">
            <CardContent className="py-12 text-center space-y-3">
              <ClipboardList className="h-12 w-12 text-muted-foreground/40 mx-auto" />
              <p className="text-muted-foreground font-medium">Noch keine Aufgaben angelegt</p>
              <Button onClick={openCreate} variant="outline">
                <Plus className="h-4 w-4 mr-2" /> Erste Aufgabe anlegen
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-1.5" onDragOver={e => e.preventDefault()} onDrop={handleDrop}>
            {filteredTasks.map((task, index) => {
              const priority = PRIORITY_OPTIONS.find(p => p.value === task.priority);
              const typeInfo = getTaskTypeInfo(task.task_type);
              const TypeIcon = typeInfo.icon;
              return (
                <div key={task.id} draggable
                  onDragStart={() => handleDragStart(index)}
                  onDragOver={(e) => handleDragOver(e, index)}
                  className="flex items-start gap-3 px-4 py-3 rounded-xl border border-border bg-card hover:bg-muted/20 transition-colors"
                  data-testid={`task-${task.id}`}
                >
                  <div className="cursor-grab active:cursor-grabbing text-muted-foreground/30 mt-1 shrink-0">
                    <GripVertical className="h-4 w-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded font-semibold ${typeInfo.color}`}>
                        <TypeIcon className="h-3 w-3" />{typeInfo.label}
                      </span>
                      <span className="font-medium text-sm">{task.name}</span>
                      <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${priority?.cls}`}>{priority?.label}</span>
                      {task.is_arbeitsbeginn && <span className="text-[10px] bg-orange-100 text-orange-700 px-1.5 py-0.5 rounded font-semibold">Arbeitsbeginn</span>}
                      {task.reference_image_url && <span className="text-xs px-1.5 py-0.5 rounded-full bg-blue-100 text-blue-700 flex items-center gap-1"><ImageIcon className="h-2.5 w-2.5" />Bild</span>}
                    </div>
                    {task.description && <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">{task.description}</p>}
                    <div className="flex flex-wrap gap-1 mt-1 text-xs">
                      <span className="bg-muted px-1.5 py-0.5 rounded-full text-muted-foreground">{getTurnusLabel(task.turnus)}</span>
                      {task.ziel_uhrzeit && <span className="bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full flex items-center gap-1"><Clock className="h-3 w-3" />bis {task.ziel_uhrzeit}</span>}
                      {locations.filter(l => (task.location_ids || []).includes(l.id)).map(l => (
                        <span key={l.id} className="bg-muted px-1.5 py-0.5 rounded-full text-muted-foreground">{l.name}</span>
                      ))}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => openEdit(task)}><Edit2 className="h-3.5 w-3.5" /></Button>
                    <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-destructive hover:text-destructive" onClick={() => handleDelete(task.id)}><Trash2 className="h-3.5 w-3.5" /></Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
              </div>
            )}
          </div>
        )}

        {/* Employee-Ansicht: kein Konfigurieren-Bereich */}
        {!isConfigurator && (
          <div className="text-center py-8 text-muted-foreground" data-testid="employee-no-config">
            <ClipboardList className="h-10 w-10 mx-auto mb-3 opacity-30" />
            <p className="text-sm">Tagesaufgaben werden von Admin/Stationsleitung konfiguriert.</p>
            <p className="text-xs mt-1">Deine heutigen Aufgaben findest du unter <strong>Tagesaufgaben</strong>.</p>
          </div>
        )}
      </div>

      {/* Modal: Aufgabe anlegen / bearbeiten */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="task-modal">
          <DialogHeader>
            <DialogTitle>{editId ? 'Aufgabe bearbeiten' : 'Neue Aufgabe anlegen'}</DialogTitle>
          </DialogHeader>

          <div className="space-y-5 py-2">
            {/* Aufgaben-Typ */}
            <div className="space-y-1.5">
              <Label>Aufgaben-Typ *</Label>
              <div className="flex flex-wrap gap-2">
                {TASK_TYPE_OPTIONS.map(opt => {
                  const Icon = opt.icon;
                  return (
                    <button key={opt.value} type="button"
                      onClick={() => setForm(f => ({ ...f, task_type: opt.value }))}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-sm font-medium transition-colors ${
                        form.task_type === opt.value
                          ? `${opt.color} border-current`
                          : 'border-border text-muted-foreground hover:border-primary/40'
                      }`}
                      data-testid={`task-type-${opt.value}`}>
                      <Icon className="h-3.5 w-3.5" />{opt.label}
                    </button>
                  );
                })}
              </div>
              {form.task_type === 'backen' && <p className="text-xs text-muted-foreground">→ Verlinkt automatisch zur Backen-Planung</p>}
              {form.task_type === 'temperatur' && <p className="text-xs text-muted-foreground">→ Verlinkt automatisch zur Temperaturkontrolle, wird grün nach Eingabe</p>}
              {form.task_type === 'auffuellen' && <p className="text-xs text-muted-foreground">→ Laufendes Band: Kategorien unter "Laufende Bänder" konfigurieren</p>}
              {form.task_type === 'putzen' && <p className="text-xs text-muted-foreground">→ Laufendes Band: Zonen unter "Laufende Bänder" konfigurieren</p>}
            </div>

            {/* Name */}
            <div className="space-y-1.5">
              <Label>Name *</Label>
              <Input
                placeholder="z.B. Kühlschrank kontrollieren"
                value={form.name}
                onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                data-testid="task-name-input"
              />
            </div>

            {/* Bei Arbeitsbeginn Flag (nur Standard) */}
            {form.task_type === 'standard' && (
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={form.is_arbeitsbeginn}
                  onChange={e => setForm(f => ({ ...f, is_arbeitsbeginn: e.target.checked }))}
                  className="rounded" data-testid="is-arbeitsbeginn-checkbox" />
                <span className="text-sm font-medium">Bei Arbeitsbeginn erledigen</span>
                <span className="text-xs text-muted-foreground">(wird oben in der Liste hervorgehoben)</span>
              </label>
            )}

            {/* Beschreibung */}
            <div className="space-y-1.5">
              <Label>Beschreibung</Label>
              <Textarea
                placeholder="Optionale Details zur Aufgabe..."
                value={form.description}
                onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                rows={2}
              />
            </div>

            {/* Anschauungsbilder – Galerie (bis zu 10 Bilder) */}
            <div className="space-y-3">
              <Label className="flex items-center gap-1.5">
                <ImageIcon className="h-3.5 w-3.5 text-muted-foreground" />
                Anschauungsbilder
                <span className="text-xs text-muted-foreground font-normal">
                  (optional, max. 10 – Mitarbeiter können durchklicken)
                </span>
              </Label>

              {/* Bestehende Bilder mit Sortier-Pfeilen */}
              {multiImages.length > 0 && (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {multiImages.map((img, i) => (
                    <div key={i} className="relative group rounded-xl overflow-hidden border border-border bg-muted">
                      <img src={img.url} alt={`Bild ${i+1}`}
                        className="w-full h-24 object-cover" />
                      {/* Schrittanzeige */}
                      <div className="absolute top-1.5 left-1.5 bg-black/60 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                        {i+1}
                      </div>
                      {/* Löschen */}
                      {editId && (
                        <button type="button"
                          onClick={async () => {
                            const authH = { Authorization: `Bearer ${localStorage.getItem('token')}` };
                            try {
                              await axios.delete(`${API}/tasks/templates/${editId}/images/${i}`, { headers: authH });
                              setMultiImages(prev => {
                                const arr = [...prev]; arr.splice(i, 1);
                                return arr.map((x, j) => ({ ...x, index: j }));
                              });
                              toast.success('Bild gelöscht');
                            } catch { toast.error('Fehler beim Löschen'); }
                          }}
                          className="absolute top-1 right-1 bg-black/60 hover:bg-red-600 text-white rounded-full p-0.5"
                        >
                          <X className="h-3.5 w-3.5" />
                        </button>
                      )}
                      {/* Sortier-Pfeile */}
                      <div className="absolute bottom-6 left-0 right-0 flex justify-center gap-1">
                        {i > 0 && (
                          <button type="button"
                            onClick={async () => {
                              const authH = { Authorization: `Bearer ${localStorage.getItem('token')}` };
                              const arr = [...multiImages];
                              [arr[i-1], arr[i]] = [arr[i], arr[i-1]];
                              const updated = arr.map((x, j) => ({ ...x, index: j }));
                              setMultiImages(updated);
                              if (editId) await axios.put(`${API}/tasks/templates/${editId}`, { reference_images: updated }, { headers: authH });
                            }}
                            className="bg-black/60 hover:bg-black/80 text-white text-xs px-1.5 py-0.5 rounded"
                          >←</button>
                        )}
                        {i < multiImages.length - 1 && (
                          <button type="button"
                            onClick={async () => {
                              const authH = { Authorization: `Bearer ${localStorage.getItem('token')}` };
                              const arr = [...multiImages];
                              [arr[i], arr[i+1]] = [arr[i+1], arr[i]];
                              const updated = arr.map((x, j) => ({ ...x, index: j }));
                              setMultiImages(updated);
                              if (editId) await axios.put(`${API}/tasks/templates/${editId}`, { reference_images: updated }, { headers: authH });
                            }}
                            className="bg-black/60 hover:bg-black/80 text-white text-xs px-1.5 py-0.5 rounded"
                          >→</button>
                        )}
                      </div>
                      {/* Beschriftung */}
                      <input
                        type="text"
                        placeholder={`Schritt ${i+1}: ...`}
                        defaultValue={img.caption || ''}
                        style={{ fontSize: '12px' }}
                        className="w-full px-2 py-1 text-xs border-t border-border bg-background focus:outline-none"
                        onBlur={async (e) => {
                          if (!editId) return;
                          const authH = { Authorization: `Bearer ${localStorage.getItem('token')}` };
                          try {
                            await axios.put(
                              `${API}/tasks/templates/${editId}/images/${i}/caption?caption=${encodeURIComponent(e.target.value)}`,
                              {}, { headers: authH }
                            );
                          } catch { /* silent */ }
                        }}
                      />
                    </div>
                  ))}
                </div>
              )}

              {/* iOS-kompatibler Upload: label statt onClick() */}
              {multiImages.length < 10 && (
                <label htmlFor="multi-image-upload"
                  className={`w-full border-2 border-dashed border-border rounded-xl p-3 flex items-center gap-3 cursor-pointer transition-colors ${
                    uploadingMulti
                      ? 'opacity-60 pointer-events-none'
                      : 'text-muted-foreground hover:border-primary hover:text-primary'
                  }`}
                >
                  <Camera className="h-5 w-5 shrink-0" />
                  <div className="text-left">
                    <p className="text-sm font-medium">
                      {uploadingMulti
                        ? `Lädt hoch…`
                        : `Bilder hinzufügen (${multiImages.length}/10)`}
                    </p>
                    <p className="text-xs">JPG, PNG, WebP · mehrere Bilder gleichzeitig möglich</p>
                  </div>
                </label>
              )}

              {/* File Input – label-basiert für iOS-Kompatibilität */}
              <input
                id="multi-image-upload"
                type="file"
                accept="image/*,image/heic,image/heif"
                multiple
                className="hidden"
                onChange={async (e) => {
                  const files = Array.from(e.target.files || []);
                  if (!files.length) return;

                  // Auth-Header direkt hier definieren (nicht aus äußerem Scope)
                  const authHeaders = { Authorization: `Bearer ${localStorage.getItem('token')}` };

                  if (!editId) {
                    // Neu-Erstellungs-Modus: lokal speichern
                    const newImgs = files.slice(0, 10 - multiImages.length).map((f, j) => ({
                      url: URL.createObjectURL(f),
                      caption: '',
                      _file: f,
                      index: multiImages.length + j,
                    }));
                    setMultiImages(prev => [...prev, ...newImgs]);
                    e.target.value = '';
                    return;
                  }

                  // Bearbeiten-Modus: sofort hochladen
                  const toUpload = files.slice(0, 10 - multiImages.length);
                  setUploadingMulti(true);
                  try {
                    const results = [];
                    for (const f of toUpload) {
                      try {
                        const fd = new FormData();
                        fd.append('file', f);
                        const r = await axios.post(
                          `${API}/tasks/templates/${editId}/upload-images?caption=`,
                          fd,
                          { headers: { ...authHeaders, 'Content-Type': 'multipart/form-data' } }
                        );
                        results.push(r.data.image);
                      } catch (uploadErr) {
                        const msg = uploadErr?.response?.data?.detail || uploadErr?.message || 'Unbekannter Fehler';
                        toast.error(`Fehler bei "${f.name}": ${msg}`);
                      }
                    }
                    if (results.length) {
                      setMultiImages(prev => [...prev, ...results]);
                      toast.success(`${results.length} Bild${results.length > 1 ? 'er' : ''} hochgeladen ✅`);
                    }
                  } finally {
                    setUploadingMulti(false); // Immer zurücksetzen
                    e.target.value = '';
                  }
                }}
              />
            </div>
            <div className="space-y-1.5">
              <Label>Priorität</Label>
              <div className="flex flex-wrap gap-2">
                {PRIORITY_OPTIONS.map(p => (
                  <button
                    key={p.value}
                    type="button"
                    onClick={() => setForm(f => ({ ...f, priority: p.value }))}
                    className={`px-3 py-1.5 rounded-full text-sm font-medium border-2 transition-all ${
                      form.priority === p.value
                        ? `${p.cls} border-current`
                        : 'border-transparent bg-muted text-muted-foreground hover:bg-muted/80'
                    }`}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Standort(e) */}
            <div className="space-y-1.5">
              <Label>Standort(e) * <span className="text-muted-foreground font-normal">(Mehrfachauswahl)</span></Label>
              {locations.length === 0 ? (
                <p className="text-sm text-muted-foreground">Keine Standorte verfügbar</p>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {locations.map(l => (
                    <button
                      key={l.id}
                      type="button"
                      onClick={() => toggleLocation(l.id)}
                      data-testid={`location-toggle-${l.id}`}
                      className={`px-3 py-1.5 rounded-lg text-sm font-medium border-2 transition-all ${
                        form.location_ids.includes(l.id)
                          ? 'bg-primary text-primary-foreground border-primary'
                          : 'border-border bg-background text-foreground hover:bg-muted'
                      }`}
                    >
                      {l.name}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Turnus */}
            <div className="space-y-2">
              <Label>Turnus (Wiederholung) *</Label>
              <div className="flex flex-wrap gap-2">
                {TURNUS_OPTIONS.map(t => (
                  <button
                    key={t.value}
                    type="button"
                    onClick={() => setForm(f => ({ ...f, turnus: { ...f.turnus, typ: t.value } }))}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium border-2 transition-all ${
                      form.turnus.typ === t.value
                        ? 'bg-primary text-primary-foreground border-primary'
                        : 'border-border bg-background text-foreground hover:bg-muted'
                    }`}
                  >
                    {t.label}
                  </button>
                ))}
              </div>

              {/* Wöchentlich: Wochentage */}
              {form.turnus.typ === 'wöchentlich' && (
                <div className="mt-2 space-y-1.5">
                  <Label className="text-xs text-muted-foreground">Wochentage auswählen:</Label>
                  <div className="flex flex-wrap gap-1.5">
                    {WOCHENTAGE.map(w => (
                      <button
                        key={w.value}
                        type="button"
                        onClick={() => toggleWochentag(w.value)}
                        className={`w-10 h-10 rounded-full text-sm font-semibold border-2 transition-all ${
                          form.turnus.wochentage.includes(w.value)
                            ? 'bg-primary text-primary-foreground border-primary'
                            : 'border-border bg-background text-foreground hover:bg-muted'
                        }`}
                      >
                        {w.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Monatlich: Tag im Monat */}
              {form.turnus.typ === 'monatlich' && (
                <div className="mt-2 space-y-1.5">
                  <Label className="text-xs text-muted-foreground">Tag im Monat (1–31):</Label>
                  <Input
                    type="number"
                    min={1}
                    max={31}
                    value={form.turnus.monatstag || 1}
                    onChange={e => setForm(f => ({
                      ...f,
                      turnus: { ...f.turnus, monatstag: parseInt(e.target.value) || 1 }
                    }))}
                    className="w-24"
                  />
                </div>
              )}

              {/* Einmalig: Datum */}
              {form.turnus.typ === 'einmalig' && (
                <div className="mt-2 space-y-1.5">
                  <Label className="text-xs text-muted-foreground">Datum:</Label>
                  <Input
                    type="date"
                    value={form.turnus.einmalig_datum || ''}
                    onChange={e => setForm(f => ({
                      ...f,
                      turnus: { ...f.turnus, einmalig_datum: e.target.value }
                    }))}
                    className="w-44"
                  />
                </div>
              )}
            </div>

            {/* Deadline / Frist */}
            <div className="space-y-1.5">
              <Label>Deadline / Frist <span className="text-muted-foreground font-normal">(optional)</span></Label>
              <div className="flex items-center gap-2">
                <Input
                  type="time"
                  value={form.deadline_time}
                  onChange={e => setForm(f => ({ ...f, deadline_time: e.target.value }))}
                  className="w-36"
                />
                {form.deadline_time && (
                  <button
                    type="button"
                    onClick={() => setForm(f => ({ ...f, deadline_time: '' }))}
                    className="text-xs text-muted-foreground hover:text-foreground"
                  >
                    löschen
                  </button>
                )}
              </div>
            </div>

            {/* Ziel-Uhrzeit (weiches Planungsziel, kein Lock) */}
            <div className="space-y-1.5">
              <Label>Ziel-Uhrzeit <span className="text-muted-foreground font-normal">(geplante Erledigungszeit – kein Lock, nur Überfälligkeits-Anzeige)</span></Label>
              <div className="flex items-center gap-2">
                <Input
                  type="time"
                  value={form.ziel_uhrzeit || ''}
                  onChange={e => setForm(f => ({ ...f, ziel_uhrzeit: e.target.value }))}
                  className="w-36"
                  data-testid="ziel-uhrzeit-input"
                />
                {form.ziel_uhrzeit && (
                  <button
                    type="button"
                    onClick={() => setForm(f => ({ ...f, ziel_uhrzeit: '' }))}
                    className="text-xs text-muted-foreground hover:text-foreground"
                  >
                    löschen
                  </button>
                )}
              </div>
              {form.ziel_uhrzeit && (
                <p className="text-xs text-amber-600 dark:text-amber-400">
                  Aufgabe wird rot markiert wenn sie um {form.ziel_uhrzeit} Uhr noch nicht erledigt ist.
                </p>
              )}
            </div>

            {/* Ab Uhrzeit (Richtzeit-Anzeige — kein Hard-Lock mehr) */}
            <div className="space-y-1.5">
              <Label>Ab Uhrzeit <span className="text-muted-foreground font-normal">(optional – Richtzeit, ab wann die Aufgabe sinnvoll ist; früher erledigen ist immer erlaubt)</span></Label>
              <div className="flex items-center gap-2">
                <Input
                  type="time"
                  value={form.ab_uhrzeit}
                  onChange={e => setForm(f => ({ ...f, ab_uhrzeit: e.target.value }))}
                  className="w-36"
                  data-testid="ab-uhrzeit-input"
                />
                {form.ab_uhrzeit && (
                  <button
                    type="button"
                    onClick={() => setForm(f => ({ ...f, ab_uhrzeit: '' }))}
                    className="text-xs text-muted-foreground hover:text-foreground"
                  >
                    löschen
                  </button>
                )}
              </div>
              {form.ab_uhrzeit && (
                <p className="text-xs text-blue-600 dark:text-blue-400">
                  Mitarbeiter können diese Aufgabe erst ab {form.ab_uhrzeit} Uhr erledigen.
                </p>
              )}
            </div>

            {/* Foto erforderlich */}
            <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
              <input
                type="checkbox"
                id="photo-required"
                checked={form.photo_required}
                onChange={e => setForm(f => ({ ...f, photo_required: e.target.checked }))}
                className="h-4 w-4 rounded"
              />
              <Label htmlFor="photo-required" className="cursor-pointer text-sm">
                Foto-Nachweis erforderlich
              </Label>
            </div>

            {/* Blockiert andere Aufgaben (opt-in) – SICHERHEITSKRITISCH */}
            <div className={`flex items-start gap-3 p-4 rounded-lg border-2 transition-colors ${
              form.blocks_others
                ? 'bg-amber-50 dark:bg-amber-900/20 border-amber-400 dark:border-amber-600'
                : 'border-amber-200 dark:border-amber-800 bg-amber-50/30 dark:bg-amber-900/10'
            }`}>
              <input
                type="checkbox"
                id="blocks-others"
                checked={form.blocks_others || false}
                onChange={e => setForm(f => ({ ...f, blocks_others: e.target.checked }))}
                className="h-5 w-5 rounded mt-0.5 accent-amber-600"
                data-testid="blocks-others-check"
              />
              <div>
                <Label htmlFor="blocks-others" className="cursor-pointer text-sm font-semibold text-amber-700 dark:text-amber-400">
                  Blockiert andere Aufgaben bis erledigt
                </Label>
                <p className="text-xs text-amber-600/80 dark:text-amber-400/80 mt-0.5">
                  Für sicherheitskritische Aufgaben (z.B. Tresorübergabe, Kassenlade). Alle anderen Aufgaben sind gesperrt, bis diese erledigt ist.
                </p>
              </div>
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setModalOpen(false)}>Abbrechen</Button>
            <Button onClick={handleSave} disabled={saving} data-testid="save-task-btn">
              {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              {editId ? 'Aktualisieren' : 'Erstellen'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};

export default TaskTypes;
